// src/components/shared/VehicleCard/VehicleCard.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../../../hooks/useAnalytics.js';
import { trackClick as trackPref } from '../../../utils/userPrefs.js';
import { listingService } from '../../../services/listingService.js';
import { useAuth } from '../../../context/AuthContext.js';
import './VehicleCard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://bw-car-culture-api.vercel.app';

const VehicleCard = ({ car, onShare, compact = false }) => {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { user } = useAuth();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [hasBeenViewed, setHasBeenViewed] = useState(false);
  const [dealerImageError, setDealerImageError] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);
  
  // TRUE ZOOM: Enhanced zoom functionality state
  const [zoomLevel, setZoomLevel] = useState(1); // Start at 100% (normal cropped view)
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [coverPos, setCoverPos] = useState({ x: 50, y: 50 }); // object-position % at zoom=1
  const [showZoomControls, setShowZoomControls] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastPanPosition, setLastPanPosition] = useState({ x: 0, y: 0 });
  
  // TRUE ZOOM: Image dimension tracking
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const [isImageFullyLoaded, setIsImageFullyLoaded] = useState(false);
  
  // Refs for zoom functionality
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const dragTimeoutRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const navigationTimeoutRef = useRef(null);
  const zoomLevelRef = useRef(1); // kept in sync for non-passive listeners

  // Review flip state
  const [isFlipped, setIsFlipped] = useState(false);
  const [backTab, setBackTab] = useState('reviews'); // 'reviews' | 'finance' | 'service-history' | 'video'
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
  const [previewCount, setPreviewCount] = useState(null); // null = not yet loaded
  const [newRating, setNewRating] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Replies
  const [replyingTo, setReplyingTo] = useState(null); // reviewId
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  // Reactions
  const [reactingTo, setReactingTo] = useState(null); // prevent double-tap

  // FIXED: Enhanced checkFailedImage function to include type
  const checkFailedImage = useCallback((url, type = 'general') => {
    try {
      const failedImages = JSON.parse(localStorage.getItem('failedImages') || '{}');
      const key = `${url}_${type}`;
      const failureTime = failedImages[key];
      
      if (!failureTime) return false;
      
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      if (failureTime < oneHourAgo) {
        delete failedImages[key];
        localStorage.setItem('failedImages', JSON.stringify(failedImages));
        return false;
      }
      
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  const markFailedImage = useCallback((url, type = 'general') => {
    try {
      const failedImages = JSON.parse(localStorage.getItem('failedImages') || '{}');
      const key = `${url}_${type}`;
      failedImages[key] = Date.now();
      
      const keys = Object.keys(failedImages);
      if (keys.length > 100) {
        const oldestKey = keys.sort((a, b) => failedImages[a] - failedImages[b])[0];
        delete failedImages[oldestKey];
      }
      localStorage.setItem('failedImages', JSON.stringify(failedImages));
    } catch (e) {
      // Ignore localStorage errors
    }
  }, []);

  const safeGetStringId = useCallback((id) => {
    if (!id) return null;
    
    if (typeof id === 'string' && id !== '[object Object]') {
      return id;
    }
    
    if (typeof id === 'object') {
      if (id._id) {
        if (typeof id._id === 'string') {
          return id._id;
        } else if (id._id.toString) {
          return id._id.toString();
        }
      }
      
      if (id.id) {
        if (typeof id.id === 'string') {
          return id.id;
        } else if (id.id.toString) {
          return id.id.toString();
        }
      }
      
      if (id.toString && id.toString() !== '[object Object]') {
        return id.toString();
      }
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.error("Failed to extract valid ID from:", id);
    }
    return null;
  }, []);

  // TRUE ZOOM: Track container dimensions
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateContainerDimensions = () => {
      const rect = container.getBoundingClientRect();
      setContainerDimensions({ width: rect.width, height: rect.height });
    };

    // Initial measurement
    updateContainerDimensions();

    // Set up ResizeObserver for responsive updates
    if (window.ResizeObserver) {
      resizeObserverRef.current = new ResizeObserver(updateContainerDimensions);
      resizeObserverRef.current.observe(container);
    } else {
      // Fallback for older browsers
      window.addEventListener('resize', updateContainerDimensions);
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      } else {
        window.removeEventListener('resize', updateContainerDimensions);
      }
    };
  }, []);

  // Keep zoomLevelRef in sync (used by non-passive wheel/touch listeners)
  useEffect(() => { zoomLevelRef.current = zoomLevel; }, [zoomLevel]);

  // TRUE ZOOM: Reset zoom when image changes
  useEffect(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
    setCoverPos({ x: 50, y: 50 });
    setShowZoomControls(false);
    setIsDragging(false);
    setIsImageFullyLoaded(false);
    setImageDimensions({ width: 0, height: 0 });
  }, [activeImageIndex, car]);

  // TRUE ZOOM: Calculate image display properties
  const imageDisplayProps = useMemo(() => {
    if (!isImageFullyLoaded || !imageDimensions.width || !containerDimensions.width) {
      return {
        objectFit: 'cover',
        width: '100%',
        height: '100%',
        transform: 'none',
        maxWidth: 'none',
        maxHeight: 'none'
      };
    }

    // At zoom level 1.0: normal cropped view (object-fit: cover) — pan via objectPosition
    if (zoomLevel === 1) {
      return {
        objectFit: 'cover',
        width: '100%',
        height: '100%',
        transform: 'none',
        maxWidth: 'none',
        maxHeight: 'none',
        objectPosition: `${coverPos.x}% ${coverPos.y}%`,
        transition: 'object-position 0.08s ease-out'
      };
    }

    // Above zoom level 1.0: show full image with true zoom
    const containerAspect = containerDimensions.width / containerDimensions.height;
    const imageAspect = imageDimensions.width / imageDimensions.height;
    
    let baseWidth, baseHeight;
    
    // Calculate base size to fit full image in container
    if (imageAspect > containerAspect) {
      // Image is wider - fit to container width
      baseWidth = containerDimensions.width;
      baseHeight = containerDimensions.width / imageAspect;
    } else {
      // Image is taller - fit to container height  
      baseHeight = containerDimensions.height;
      baseWidth = containerDimensions.height * imageAspect;
    }

    // Apply zoom scaling
    const displayWidth = baseWidth * (zoomLevel - 1 + 1); // Zoom from actual image size
    const displayHeight = baseHeight * (zoomLevel - 1 + 1);
    
    // Calculate transform for panning
    const transformX = panPosition.x / zoomLevel;
    const transformY = panPosition.y / zoomLevel;

    return {
      objectFit: 'contain',
      width: `${displayWidth}px`,
      height: `${displayHeight}px`,
      maxWidth: 'none',
      maxHeight: 'none',
      transform: `translate(${transformX}px, ${transformY}px)`,
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: `${-displayHeight / 2}px`,
      marginLeft: `${-displayWidth / 2}px`
    };
  }, [zoomLevel, panPosition, coverPos, imageDimensions, containerDimensions, isImageFullyLoaded]);

  // TRUE ZOOM: Enhanced zoom functionality with 5% increments
  const handleZoomIn = useCallback((e) => {
    e.stopPropagation();
    setZoomLevel(prev => Math.min(prev + 0.05, 4)); // 5% increments, Max zoom 4x
    
    try {
      analytics.trackClick('image_zoom', 'zoom_in', {
        listingId: car._id,
        zoomLevel: zoomLevel + 0.05,
        imageIndex: activeImageIndex
      });
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
  }, [zoomLevel, analytics, car, activeImageIndex]);

  const handleZoomOut = useCallback((e) => {
    e.stopPropagation();
    const newZoom = Math.max(zoomLevel - 0.05, 0.5); // 5% decrements, Min zoom 50%
    setZoomLevel(newZoom);
    
    // Reset pan if back to normal cropped view
    if (newZoom === 1) {
      setPanPosition({ x: 0, y: 0 });
    }
    
    try {
      analytics.trackClick('image_zoom', 'zoom_out', {
        listingId: car._id,
        zoomLevel: newZoom,
        imageIndex: activeImageIndex
      });
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
  }, [zoomLevel, analytics, car, activeImageIndex]);

  const handleZoomReset = useCallback((e) => {
    e.stopPropagation();
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
    
    try {
      analytics.trackClick('image_zoom', 'reset', {
        listingId: car._id,
        imageIndex: activeImageIndex
      });
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
  }, [analytics, car, activeImageIndex]);

  // TRUE ZOOM: Enhanced mouse drag handlers
  const handleMouseDown = useCallback((e) => {
    if (zoomLevel <= 1) return; // Only allow panning when zoomed beyond normal view
    
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setLastPanPosition(panPosition);
    
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
  }, [zoomLevel, panPosition]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || zoomLevel <= 1) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    // Calculate max pan limits based on actual image display size
    if (!imageDimensions.width || !containerDimensions.width) return;
    
    const containerAspect = containerDimensions.width / containerDimensions.height;
    const imageAspect = imageDimensions.width / imageDimensions.height;
    
    let baseWidth, baseHeight;
    if (imageAspect > containerAspect) {
      baseWidth = containerDimensions.width;
      baseHeight = containerDimensions.width / imageAspect;
    } else {
      baseHeight = containerDimensions.height;
      baseWidth = containerDimensions.height * imageAspect;
    }
    
    const displayWidth = baseWidth * zoomLevel;
    const displayHeight = baseHeight * zoomLevel;
    
    const maxPanX = Math.max(0, (displayWidth - containerDimensions.width) / 2);
    const maxPanY = Math.max(0, (displayHeight - containerDimensions.height) / 2);
    
    const newX = Math.max(-maxPanX, Math.min(maxPanX, lastPanPosition.x + deltaX));
    const newY = Math.max(-maxPanY, Math.min(maxPanY, lastPanPosition.y + deltaY));
    
    setPanPosition({ x: newX, y: newY });
  }, [isDragging, zoomLevel, dragStart, lastPanPosition, imageDimensions, containerDimensions]);

  const handleMouseUp = useCallback((e) => {
    if (!isDragging) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    dragTimeoutRef.current = setTimeout(() => {
      dragTimeoutRef.current = null;
    }, 100);
  }, [isDragging]);

  // TRUE ZOOM: Enhanced touch handlers
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      e.stopPropagation();
      
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      
      setDragStart({ distance, zoom: zoomLevel });
    } else if (e.touches.length === 1 && zoomLevel > 1) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      setLastPanPosition(panPosition);
    }
  }, [zoomLevel, panPosition]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      e.stopPropagation();
      
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      
      if (dragStart.distance) {
        const scale = distance / dragStart.distance;
        const newZoom = Math.max(0.5, Math.min(4, dragStart.zoom * scale));
        setZoomLevel(newZoom);
        
        if (newZoom === 1) {
          setPanPosition({ x: 0, y: 0 });
        }
      }
    } else if (e.touches.length === 1 && isDragging && zoomLevel > 1) {
      e.preventDefault();
      e.stopPropagation();
      
      const deltaX = e.touches[0].clientX - dragStart.x;
      const deltaY = e.touches[0].clientY - dragStart.y;
      
      // Use same pan logic as mouse
      if (!imageDimensions.width || !containerDimensions.width) return;
      
      const containerAspect = containerDimensions.width / containerDimensions.height;
      const imageAspect = imageDimensions.width / imageDimensions.height;
      
      let baseWidth, baseHeight;
      if (imageAspect > containerAspect) {
        baseWidth = containerDimensions.width;
        baseHeight = containerDimensions.width / imageAspect;
      } else {
        baseHeight = containerDimensions.height;
        baseWidth = containerDimensions.height * imageAspect;
      }
      
      const displayWidth = baseWidth * zoomLevel;
      const displayHeight = baseHeight * zoomLevel;
      
      const maxPanX = Math.max(0, (displayWidth - containerDimensions.width) / 2);
      const maxPanY = Math.max(0, (displayHeight - containerDimensions.height) / 2);
      
      const newX = Math.max(-maxPanX, Math.min(maxPanX, lastPanPosition.x + deltaX));
      const newY = Math.max(-maxPanY, Math.min(maxPanY, lastPanPosition.y + deltaY));
      
      setPanPosition({ x: newX, y: newY });
    }
  }, [isDragging, zoomLevel, dragStart, lastPanPosition, imageDimensions, containerDimensions]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    dragTimeoutRef.current = setTimeout(() => {
      dragTimeoutRef.current = null;
    }, 150);
  }, []);

  // Add global mouse event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Cover-pan: scroll wheel + single-finger drag pans object-position at zoom=1
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let touchOrigin = null;

    const onWheel = (e) => {
      if (zoomLevelRef.current > 1) return;
      e.preventDefault();
      e.stopPropagation();
      setCoverPos(prev => ({
        x: Math.max(0, Math.min(100, prev.x + e.deltaX * 0.1)),
        y: Math.max(0, Math.min(100, prev.y + e.deltaY * 0.1))
      }));
    };

    const onTouchStart = (e) => {
      if (zoomLevelRef.current > 1 || e.touches.length !== 1) { touchOrigin = null; return; }
      touchOrigin = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };

    const onTouchMove = (e) => {
      if (zoomLevelRef.current > 1 || e.touches.length !== 1 || !touchOrigin) return;
      const dx = e.touches[0].clientX - touchOrigin.x;
      const dy = e.touches[0].clientY - touchOrigin.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        e.preventDefault();
        setCoverPos(prev => ({
          x: Math.max(0, Math.min(100, prev.x - dx * 0.12)),
          y: Math.max(0, Math.min(100, prev.y - dy * 0.12))
        }));
        touchOrigin = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    const onTouchEnd = () => { touchOrigin = null; };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, []); // runs once; reads zoomLevelRef for current zoom level

  // Get reliable image URL with fallbacks
  const getImageUrl = useCallback(() => {
    if (!car || !car.images || !Array.isArray(car.images) || car.images.length === 0) {
      return '/images/placeholders/car.jpg';
    }
    
    const currentImage = car.images[activeImageIndex] || car.images[0];
    
    if (checkFailedImage(currentImage)) {
      return '/images/placeholders/car.jpg';
    }
    
    try {
      if (typeof currentImage === 'object' && currentImage.url) {
        let imageUrl = currentImage.url;
        if (imageUrl.includes('/images/images/')) {
          imageUrl = imageUrl.replace(/\/images\/images\//g, '/images/');
        }
        return imageUrl;
      }
      
      if (typeof currentImage === 'string') {
        if (currentImage.startsWith('http://') || currentImage.startsWith('https://')) {
          let imageUrl = currentImage;
          if (imageUrl.includes('/images/images/')) {
            imageUrl = imageUrl.replace(/\/images\/images\//g, '/images/');
          }
          return imageUrl;
        }
        
        const imageUrl = currentImage.startsWith('/') ? currentImage : `/${currentImage}`;
        if (imageUrl.includes('/images/images/')) {
          return imageUrl.replace(/\/images\/images\//g, '/images/');
        }
        return imageUrl;
      }
      
      return '/images/placeholders/car.jpg';
    } catch (error) {
      console.error('Error processing image URL:', error);
      return '/images/placeholders/car.jpg';
    }
  }, [car, activeImageIndex, checkFailedImage]);

  // Enhanced dealer logo URL function
  const getDealerImageUrl = useCallback((type = 'logo') => {
    try {
      if (!car || !car.dealer) {
        return `/images/placeholders/${type === 'logo' ? 'dealer-logo' : 'dealer-avatar'}.jpg`;
      }
      
      let imageUrl = null;
      
      if (type === 'logo') {
        imageUrl = car.dealer.profile?.logo || car.dealer.logo || car.dealer.profilePicture;
      } else {
        imageUrl = car.dealer.profile?.avatar || car.dealer.avatar || car.dealer.profilePicture || car.dealer.logo;
      }
      
      if (imageUrl) {
        if (typeof imageUrl === 'string' && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
          return imageUrl;
        }
        
        if (typeof imageUrl === 'object' && imageUrl.url) {
          return imageUrl.url;
        }
        
        if (typeof imageUrl === 'string') {
          const fullUrl = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
          return fullUrl.replace(/\/images\/images\//g, '/images/');
        }
      }
      
      return `/images/placeholders/${type === 'logo' ? 'dealer-logo' : 'dealer-avatar'}.jpg`;
    } catch (error) {
      console.error(`Error getting dealer ${type} URL:`, error);
      return `/images/placeholders/${type === 'logo' ? 'dealer-logo' : 'dealer-avatar'}.jpg`;
    }
  }, [car]);

  // Calculate savings information
  const calculateSavings = useMemo(() => {
    if (!car || !car.priceOptions) return null;
    
    const { originalPrice, savingsAmount, savingsPercentage, showSavings, exclusiveDeal, savingsDescription, savingsValidUntil } = car.priceOptions;
    
    if (!showSavings) return null;
    
    if (savingsAmount && savingsAmount > 0) {
      return {
        amount: savingsAmount,
        percentage: savingsPercentage || Math.round((savingsAmount / (car.price + savingsAmount)) * 100),
        originalPrice: originalPrice || (car.price + savingsAmount),
        isExclusive: exclusiveDeal || false,
        description: savingsDescription || null,
        validUntil: savingsValidUntil || null
      };
    }
    
    if (originalPrice && originalPrice > car.price) {
      const savings = originalPrice - car.price;
      return {
        amount: savings,
        percentage: Math.round((savings / originalPrice) * 100),
        originalPrice: originalPrice,
        isExclusive: exclusiveDeal || false,
        description: savingsDescription || null,
        validUntil: savingsValidUntil || null
      };
    }
    
    return null;
  }, [car]);

  // ENHANCED: Better private seller detection and dealer info processing
  const dealer = useMemo(() => {
    if (!car) return null;
    
    if (car.dealer) {
      let dealerId = null;
      
      if (car.dealerId) {
        dealerId = safeGetStringId(car.dealerId);
      } else if (car.dealer._id) {
        dealerId = safeGetStringId(car.dealer._id);
      } else if (car.dealer.id) {
        dealerId = safeGetStringId(car.dealer.id);
      }
      
      const isMarketplace =
        car.dealer.sellerType === 'marketplace' ||
        car.sellerType === 'marketplace' ||
        (car.dealer.businessName || '').toLowerCase().includes('bw car culture marketplace');

      const isPrivateSeller = !isMarketplace && (
        car.dealer.sellerType === 'private' ||
        car.dealer.businessType === 'private' ||
        (car.dealer.privateSeller &&
         car.dealer.privateSeller.firstName &&
         car.dealer.privateSeller.lastName) ||
        (car.dealer.businessName &&
         (car.dealer.businessName.includes('Private') ||
          car.dealer.businessName.match(/^[A-Z][a-z]+ [A-Z][a-z]+$/))) ||
        (!car.dealer.businessType && car.dealer.name && !car.dealer.businessName)
      );

      let displayName = 'Unknown Seller';
      let contactName = null;
      let sellerTypeLabel = 'Dealership';
      
      if (isPrivateSeller) {
        sellerTypeLabel = 'Private Seller';
        
        if (car.dealer.privateSeller && car.dealer.privateSeller.firstName && car.dealer.privateSeller.lastName) {
          displayName = `${car.dealer.privateSeller.firstName} ${car.dealer.privateSeller.lastName}`;
          contactName = displayName;
        } else if (car.dealer.businessName && !car.dealer.businessName.toLowerCase().includes('dealership')) {
          displayName = car.dealer.businessName;
          contactName = displayName;
        } else if (car.dealer.name) {
          displayName = car.dealer.name;
          contactName = displayName;
        } else {
          displayName = 'Private Seller';
          contactName = 'Private Seller';
        }
      } else {
        sellerTypeLabel = 'Dealership';
        displayName = car.dealer.businessName || car.dealer.name || 'Dealership';
        contactName = car.dealer.name || 'Sales Team';
      }
      
      return {
        id: dealerId,
        name: contactName,
        businessName: displayName,
        sellerType: isMarketplace ? 'marketplace' : (isPrivateSeller ? 'private' : 'dealership'),
        sellerTypeLabel: isMarketplace ? 'Marketplace' : sellerTypeLabel,
        marketplace: isMarketplace ? {
          sourceCountry: car.marketplace?.country || car.dealer?.location?.country || car.location?.country || null,
          estimatedLandedPrice: car.marketplace?.estimatedLandedPrice || null,
          availability: car.marketplace?.availability || 'available',
          verificationBadges: car.marketplace?.verificationBadges || ['source'],
        } : null,
        logo: car.dealer.logo || car.dealer.profile?.logo || car.dealer.profile?.avatar || car.dealer.avatar || car.dealer.profilePicture,
        avatar: car.dealer.avatar || car.dealer.logo || car.dealer.profile?.logo || car.dealer.profile?.avatar || car.dealer.profilePicture,
        profilePicture: car.dealer.profilePicture || car.dealer.profile?.logo || car.dealer.profile?.avatar || car.dealer.logo || car.dealer.avatar,
        location: {
          city: car.dealer.location?.city || car.location?.city || 'Unknown Location',
          state: car.dealer.location?.state || car.location?.state || '',
          country: car.dealer.location?.country || car.location?.country || ''
        },
        verification: {
          isVerified: !!car.dealer.verification?.isVerified || car.dealer.verification?.status === 'verified'
        },
        contact: {
          phone: [car.dealer.contact?.phone, car.dealerId?.contact?.phone, car.dealer.phone, car.dealerId?.phone, car.dealer.contactPhone, car.contact?.phone, car.contact?.whatsapp].find(v => v && v !== 'N/A' && v.trim() !== '') || null,
          whatsapp: [car.dealer.contact?.whatsapp, car.dealerId?.contact?.whatsapp, car.dealer.contact?.phone, car.dealerId?.contact?.phone, car.contact?.whatsapp, car.contact?.phone].find(v => v && v !== 'N/A' && v.trim() !== '') || null,
          email: [car.dealer.contact?.email, car.dealerId?.contact?.email, car.dealer.email, car.dealer.contactEmail, car.contact?.email].find(v => v && v !== 'N/A' && v.trim() !== '') || null,
          website: !isPrivateSeller ? ([car.dealer.contact?.website, car.dealerId?.contact?.website, car.dealer.website].find(v => v && v !== 'N/A' && v.trim() !== '') || null) : null
        },
        privateSeller: isPrivateSeller ? {
          firstName: car.dealer.privateSeller?.firstName || null,
          lastName: car.dealer.privateSeller?.lastName || null,
          preferredContactMethod: car.dealer.privateSeller?.preferredContactMethod || 'both',
          canShowContactInfo: car.dealer.privateSeller?.canShowContactInfo !== false
        } : null,
        businessType: !isPrivateSeller ? car.dealer.businessType : null,
        workingHours: !isPrivateSeller ? car.dealer.workingHours : null
      };
    }
    
    if (car.dealerId) {
      const dealerId = safeGetStringId(car.dealerId);

      return {
        id: dealerId,
        name: car.contact?.sellerName || 'Contact Seller',
        businessName: car.businessInfo?.businessName || 'Vehicle Seller',
        sellerType: car.sellerType || 'dealership',
        sellerTypeLabel: 'Dealership',
        logo: '/images/placeholders/dealer-logo.jpg',
        location: {
          city: car.location?.city || 'Unknown Location',
          state: car.location?.state || '',
          country: car.location?.country || ''
        },
        verification: {
          isVerified: false
        },
        contact: {
          phone: car.contact?.phone || car.contact?.whatsapp || null,
          whatsapp: car.contact?.whatsapp || car.contact?.phone || null,
          email: car.contact?.email || null,
          website: null,
          preferredContactMethod: car.contact?.preferredContactMethod || 'both'
        },
        privateSeller: null,
        businessType: null,
        workingHours: null
      };
    }

    return {
      id: null,
      name: car.contact?.sellerName || 'Private Seller',
      businessName: car.contact?.sellerName || 'Private Seller',
      sellerType: car.sellerType || 'private',
      sellerTypeLabel: 'Private Seller',
      logo: '/images/placeholders/private-seller-avatar.jpg',
      location: {
        city: car.location?.city || 'Unknown Location',
        state: car.location?.state || '',
        country: car.location?.country || ''
      },
      verification: {
        isVerified: false
      },
      contact: {
        phone: car.contact?.phone || car.contact?.whatsapp || null,
        whatsapp: car.contact?.whatsapp || car.contact?.phone || null,
        email: car.contact?.email || null,
        website: null,
        preferredContactMethod: car.contact?.preferredContactMethod || 'both'
      },
      privateSeller: {
        firstName: null,
        lastName: null,
        preferredContactMethod: car.contact?.preferredContactMethod || 'both',
        canShowContactInfo: true
      },
      businessType: null,
      workingHours: null
    };
  }, [car, safeGetStringId]);

  // Track listing view: card must be ≥70% visible for 1.5 s before counting.
  // Admin users are excluded — their views should not affect listing metrics.
  useEffect(() => {
    if (!car || hasBeenViewed || user?.role === 'admin') return;
    const el = cardRef.current;
    if (!el) return;

    let visibilityTimer = null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          visibilityTimer = setTimeout(async () => {
            try {
              const recorded = await listingService.incrementViewCount(car._id);
              if (!recorded) return;
              analytics.trackListingView(car._id, {
                title: car.title,
                price: car.price,
                make: car.specifications?.make,
                model: car.specifications?.model,
                year: car.specifications?.year,
                dealerId: dealer?.id,
                sellerType: dealer?.sellerType,
                savings: calculateSavings?.amount || 0,
                category: 'vehicle_sales',
                source: 'marketplace_card',
                compact: compact
              });
              setHasBeenViewed(true);
            } catch (error) {
              console.warn('View tracking failed:', error);
            }
          }, 1500);
        } else {
          clearTimeout(visibilityTimer);
        }
      },
      { threshold: 0.7 }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      clearTimeout(visibilityTimer);
    };
  }, [car, hasBeenViewed, user, analytics, dealer, calculateSavings, compact]);

  useEffect(() => {
    setImageLoadError(false);
  }, [car, activeImageIndex]);

  const handleCardClick = useCallback(() => {
    if (dragTimeoutRef.current) return;
    
    if (!car || !car._id) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Cannot navigate: Missing car ID", car);
      }
      return;
    }

    try {
      analytics.trackClick('vehicle_card', 'card', {
        listingId: car._id,
        title: car.title,
        price: car.price,
        dealerId: dealer?.id,
        sellerType: dealer?.sellerType,
        action: 'view_details'
      });
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }

    trackPref(car, 'listing');
    navigate(`/marketplace/${car.slug || car._id}`);
  }, [car, navigate, analytics, dealer]);

  // FIXED: Enhanced image container tap for mobile navigation reveal - WORKING VERSION
  const handleImageContainerClick = useCallback((e) => {
    // Always stop propagation to prevent card click
    e.stopPropagation();
    
    // Prevent if dragging or zoomed in
    if (dragTimeoutRef.current || zoomLevel > 1) return;
    
    // Check if it's a mobile device (use touch capability as well)
    const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window;
    
    // Clear any existing timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    
    if (isMobile) {
      console.log('Mobile tap detected - showing controls'); // Debug log
      
      // Force show both navigation and zoom controls
      setShowNavigation(true);
      setShowZoomControls(true);
      
      // Hide after 4 seconds (longer for mobile)
      navigationTimeoutRef.current = setTimeout(() => {
        setShowNavigation(false);
        if (zoomLevel === 1) {
          setShowZoomControls(false);
        }
      }, 4000);
    } else {
      // Desktop: toggle zoom controls
      setShowZoomControls(prev => !prev);
      setShowNavigation(false); // Ensure navigation follows hover on desktop
    }
  }, [zoomLevel]);

  const handleImageNavigation = useCallback((e, direction) => {
    e.stopPropagation();
    
    if (!car || !car.images || !Array.isArray(car.images) || car.images.length <= 1) return;
    
    try {
      analytics.trackClick('image_navigation', 'button', {
        listingId: car._id,
        direction: direction,
        currentIndex: activeImageIndex,
        totalImages: car.images.length
      });
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
    
    setActiveImageIndex(prev => {
      if (direction === 'next') {
        return prev >= car.images.length - 1 ? 0 : prev + 1;
      } else {
        return prev <= 0 ? car.images.length - 1 : prev - 1;
      }
    });
  }, [car, activeImageIndex, analytics]);

  const handleShareClick = useCallback((e) => {
    e.stopPropagation();
    
    try {
      analytics.trackClick('share_button', 'button', {
        listingId: car._id,
        title: car.title,
        price: car.price,
        dealerId: dealer?.id,
        sellerType: dealer?.sellerType,
        action: 'share'
      });
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
    
    if (onShare) {
      onShare(car);
    } else {
      if (navigator.share) {
        navigator.share({
          title: car.title,
          text: `Check out this ${car.specifications?.make} ${car.specifications?.model} for P${car.price?.toLocaleString()}`,
          url: window.location.origin + `/marketplace/${car._id}`
        });
      }
    }
  }, [car, onShare, analytics, dealer]);

  const handleReserveClick = useCallback((e) => {
    e.stopPropagation();
    
    try {
      analytics.trackClick('reserve_button', 'button', {
        listingId: car._id,
        title: car.title,
        price: car.price,
        dealerId: dealer?.id,
        sellerType: dealer?.sellerType,
        action: dealer?.sellerType === 'private' ? 'contact_seller' : 'reserve_vehicle'
      });
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
    
    const vehicleDetails = [
      `${car.title}`,
      `Price: P${car.price?.toLocaleString()}`,
      car.specifications?.year ? `Year: ${car.specifications.year}` : '',
      car.specifications?.make ? `Make: ${car.specifications.make}` : '',
      car.specifications?.model ? `Model: ${car.specifications.model}` : '',
      car.specifications?.mileage ? `Mileage: ${car.specifications.mileage}km` : '',
      car.specifications?.transmission ? `Transmission: ${car.specifications.transmission}` : '',
      car.specifications?.fuelType ? `Fuel Type: ${car.specifications.fuelType}` : '',
      car.specifications?.engineSize ? `Engine: ${car.specifications.engineSize}` : '',
      car.specifications?.drivetrain ? `Drivetrain: ${car.specifications.drivetrain.toUpperCase()}` : '',
      car.condition ? `Condition: ${car.condition}` : '',
      car.location?.city ? `Location: ${car.location.city}` : ''
    ].filter(Boolean).join('\n');
    
    const contactAction = dealer?.sellerType === 'private' ? 'contact this private seller' : 'reserve this vehicle';
    const message = `Hi! I'm interested in this vehicle from Bw Car Culture:\n\n${vehicleDetails}\n\nI'd like to ${contactAction}. Please provide more details.`;

    // Comprehensive phone lookup — user listings store contact at car.contact.*
    const preferredMethod = dealer?.contact?.preferredContactMethod
      || car.contact?.preferredContactMethod
      || dealer?.privateSeller?.preferredContactMethod
      || 'both';

    const cleanVal = (v) => (v && v !== 'N/A' && v.trim() !== '') ? v : null;

    const whatsappNum = [
      dealer?.contact?.whatsapp,
      car.contact?.whatsapp,
      car.dealer?.contact?.whatsapp,
      // populated dealerId (available on detail page)
      car.dealerId?.contact?.whatsapp,
      car.dealer?.contact?.phone,
      car.dealer?.phone,
      car.dealer?.contactPhone,
      car.dealerId?.contact?.phone,
      dealer?.contact?.phone,
      car.contact?.phone
    ].map(cleanVal).find(Boolean) || null;

    const phoneNum = [
      car.contact?.phone,
      car.dealer?.contact?.phone,
      car.dealerId?.contact?.phone,
      car.dealer?.phone,
      car.dealer?.contactPhone,
      dealer?.contact?.phone,
      whatsappNum
    ].map(cleanVal).find(Boolean) || null;

    const formatNumber = (n) => n
      ? (n.startsWith('+') ? n.replace(/\s+/g, '') : `+267${n.replace(/\s+/g, '')}`)
      : null;

    const encodedMessage = encodeURIComponent(message);

    if (preferredMethod === 'phone' && phoneNum) {
      window.open(`tel:${formatNumber(phoneNum)}`, '_self');
    } else if (whatsappNum) {
      window.open(`https://wa.me/${formatNumber(whatsappNum)}?text=${encodedMessage}`, '_blank');
    } else if (phoneNum) {
      window.open(`tel:${formatNumber(phoneNum)}`, '_self');
    } else {
      // No seller contact — open Mpho AI to assist with drafting + forwarding
      const draftMessage = `Hi! I found a vehicle on Bw Car Culture that I'm interested in:\n\n${vehicleDetails}\n\nI'd like to enquire about this vehicle. Please help me get in touch with the seller or provide more details. Thank you!`;
      window.dispatchEvent(new CustomEvent('mpho:contact-assist', {
        detail: { vehicleTitle: car.title, draftMessage }
      }));
    }
  }, [car, dealer, analytics]);

  const handleDealerClick = useCallback((e) => {
    e.stopPropagation();
    
    if (dealer?.sellerType === 'private') {
      return;
    }
    
    try {
      analytics.trackClick('dealer_info', 'link', {
        dealerId: dealer?.id,
        dealerName: dealer?.businessName,
        sellerType: dealer?.sellerType,
        listingId: car._id,
        action: 'view_dealer'
      });
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
    
    const targetId = dealer?.id || safeGetStringId(car?.dealerId) || safeGetStringId(car?.dealer?._id);
    if (targetId) {
      navigate(`/dealerships/${targetId}`);
    }
  }, [dealer, navigate, analytics, car, safeGetStringId]);

  // TRUE ZOOM: Enhanced image error handling with dimension tracking
  const handleImageError = useCallback((e) => {
    const originalSrc = e.target.src;
    if (process.env.NODE_ENV === 'development') {
      console.log(`Image failed to load: ${originalSrc}`);
    }
    markFailedImage(originalSrc);
    
    try {
      analytics.trackEvent('image_load_error', {
        category: 'system',
        metadata: {
          listingId: car._id,
          imageSrc: originalSrc,
          imageIndex: activeImageIndex
        }
      });
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
    
    if (originalSrc.includes('/api/images/s3-proxy/') || 
        originalSrc.includes('/uploads/listings/') ||
        originalSrc.includes('images/images/') ||
        originalSrc.includes('/images/placeholders/')) {
      e.target.src = '/images/placeholders/car.jpg';
      return;
    }
    
    if (originalSrc.includes('amazonaws.com')) {
      const key = originalSrc.split('.amazonaws.com/').pop();
      if (key) {
        const normalizedKey = key.replace(/images\/images\//g, 'images/');
        e.target.src = `/api/images/s3-proxy/${normalizedKey}`;
        return;
      }
    }
    
    const filename = originalSrc.split('/').pop();
    if (filename && !originalSrc.includes('/images/placeholders/')) {
      e.target.src = `/uploads/listings/${filename}`;
      return;
    }
    
    e.target.src = '/images/placeholders/car.jpg';
  }, [markFailedImage, analytics, car, activeImageIndex]);

  // TRUE ZOOM: Handle image load to get dimensions
  const handleImageLoad = useCallback((e) => {
    const img = e.target;
    setImageDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight
    });
    setIsImageFullyLoaded(true);
  }, []);

  const formatValidUntil = useCallback((date) => {
    if (!date) return null;
    
    try {
      const validDate = new Date(date);
      if (validDate > new Date()) {
        return validDate.toLocaleDateString('en-GB', { 
          day: '2-digit', 
          month: 'short', 
          year: 'numeric' 
        });
      }
    } catch (e) {
      // Invalid date
    }
    return null;
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, []);

  const fetchReviews = useCallback(async () => {
    if (!car?._id) return;
    setReviewsLoading(true);
    setReviewsError(null);
    try {
      const res = await fetch(`${API_BASE}/reviews/listing/${car._id}`);
      const data = await res.json();
      if (data.success) {
        setReviews(data.data?.reviews || []);
      } else {
        setReviewsError('Could not load reviews.');
      }
    } catch {
      setReviewsError('Could not load reviews.');
    } finally {
      setReviewsLoading(false);
    }
  }, [car]);

  // Fetch review count once on mount so the badge shows before flip
  useEffect(() => {
    if (!car?._id) return;
    const controller = new AbortController();
    fetch(`${API_BASE}/reviews/listing/${car._id}`, { signal: controller.signal })
      .then(r => r.json())
      .then(data => setPreviewCount(data.data?.reviews?.length ?? 0))
      .catch(() => {});
    return () => controller.abort();
  }, [car?._id]);

  // Keep previewCount in sync after reviews are loaded from flip
  useEffect(() => {
    if (reviews.length > 0) setPreviewCount(reviews.length);
  }, [reviews]);

  const handleFlip = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isFlipped) fetchReviews();
    setIsFlipped(f => !f);
  }, [isFlipped, fetchReviews]);

  const handleFlipToTab = useCallback((tab, e) => {
    e.stopPropagation();
    e.preventDefault();
    setBackTab(tab);
    if (!isFlipped) { fetchReviews(); setIsFlipped(true); }
  }, [isFlipped, fetchReviews]);

  const handleSubmitReview = useCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!newRating || newComment.trim().length < 10 || !car?._id) return;
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (!token) { setSubmitError('Please log in to leave a review.'); return; }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch(`${API_BASE}/reviews/listing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ listingId: car._id, rating: newRating, review: newComment.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setSubmitSuccess(true);
        setNewRating(0);
        setNewComment('');
        await fetchReviews();
        setTimeout(() => setSubmitSuccess(false), 3000);
      } else {
        setSubmitError(data.message || 'Failed to submit review.');
      }
    } catch {
      setSubmitError('Network error — please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [newRating, newComment, car, fetchReviews]);

  const handleSubmitReply = useCallback(async (reviewId) => {
    if (!replyText.trim() || !reviewId) return;
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (!token) { setSubmitError('Please log in to reply.'); return; }
    setSubmittingReply(true);
    try {
      const res = await fetch(`${API_BASE}/reviews/listing/${reviewId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: replyText.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setReplyingTo(null);
        setReplyText('');
        await fetchReviews();
      }
    } catch { /* silent */ } finally {
      setSubmittingReply(false);
    }
  }, [replyText, fetchReviews]);

  const handleReact = useCallback(async (reviewId, type, replyId = null) => {
    const key = `${reviewId}-${replyId || ''}-${type}`;
    if (reactingTo === key) return;
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (!token) { setSubmitError('Please log in to react.'); return; }
    setReactingTo(key);
    try {
      const url = replyId
        ? `${API_BASE}/reviews/listing/${reviewId}/replies/${replyId}/react`
        : `${API_BASE}/reviews/listing/${reviewId}/react`;
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ type })
      });
      await fetchReviews();
    } catch { /* silent */ } finally {
      setReactingTo(null);
    }
  }, [reactingTo, fetchReviews]);

  if (!car) return null;

  return (
    <div className="vc-card-wrapper">
    <div
      ref={cardRef}
      className={`vc-card ${compact ? 'compact' : ''} ${isFlipped ? 'vc-flipped' : ''}`}
      onClick={!isFlipped ? handleCardClick : undefined}
      data-status={car.status || 'active'}
    >
      <div className="vc-card-flipper">
      <div className="vc-card-face vc-card-front">
      <div 
        className={`vc-image-container ${showNavigation ? 'show-navigation' : ''} ${showZoomControls ? 'show-zoom-controls' : ''} ${isDragging ? 'dragging' : ''}`}
        onClick={handleImageContainerClick}
        onTouchStart={(e) => {
          // Handle both tap for controls and pinch/drag for zoom
          if (e.touches.length === 1 && !zoomLevel || zoomLevel === 1) {
            // Single touch - might be a tap for controls
            const touchStartTime = Date.now();
            const touchStartX = e.touches[0].clientX;
            const touchStartY = e.touches[0].clientY;
            
            const handleTouchEndForTap = (endEvent) => {
              const touchEndTime = Date.now();
              const touchEndX = endEvent.changedTouches[0].clientX;
              const touchEndY = endEvent.changedTouches[0].clientY;
              
              // Check if it was a tap (short duration, minimal movement)
              const duration = touchEndTime - touchStartTime;
              const distance = Math.sqrt(
                Math.pow(touchEndX - touchStartX, 2) + 
                Math.pow(touchEndY - touchStartY, 2)
              );
              
              if (duration < 300 && distance < 10) {
                // It's a tap - show controls
                handleImageContainerClick(endEvent);
              }
              
              // Clean up listener
              e.target.removeEventListener('touchend', handleTouchEndForTap);
            };
            
            e.target.addEventListener('touchend', handleTouchEndForTap);
          }
          
          // Continue with normal touch handling for zoom
          handleTouchStart(e);
        }}
        ref={containerRef}
      >
        <div className="vc-image-wrapper">
          <img 
            ref={imageRef}
            src={getImageUrl()} 
            alt={car.title || 'Vehicle'} 
            className="vc-image"
            style={imageDisplayProps}
            loading="lazy"
            onError={handleImageError}
            onLoad={handleImageLoad}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            draggable={false}
          />
          
          {/* SOLD BADGE OVERLAY */}
          {car.status === 'sold' && (
            <div className="vc-sold-overlay">
              <div className="vc-sold-badge">
                <span className="vc-sold-text">SOLD</span>
                {car.sold?.date && (
                  <span className="vc-sold-date">
                    {new Date(car.sold.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* PENDING BADGE OVERLAY */}
          {car.status === 'pending' && (
            <div className="vc-pending-overlay">
              <div className="vc-pending-badge">
                <span className="vc-pending-text">PENDING</span>
              </div>
            </div>
          )}
          
          {/* TRUE ZOOM: Enhanced Zoom Controls - ALWAYS RENDERED (like navigation arrows) */}
          <div className={`vc-zoom-controls ${zoomLevel !== 1 ? 'zoomed' : ''} ${showNavigation ? 'show' : ''}`}>
            <button 
              className="vc-zoom-btn zoom-in" 
              onClick={handleZoomIn}
              onTouchEnd={(e) => { e.stopPropagation(); handleZoomIn(e); }}
              disabled={zoomLevel >= 4}
              aria-label="Zoom in"
              title="Zoom in"
            >
              +
            </button>
            <span className="vc-zoom-level">{Math.round(zoomLevel * 100)}%</span>
            <button 
              className="vc-zoom-btn zoom-out" 
              onClick={handleZoomOut}
              onTouchEnd={(e) => { e.stopPropagation(); handleZoomOut(e); }}
              disabled={zoomLevel <= 0.5}
              aria-label="Zoom out"
              title="Zoom out"
            >
              -
            </button>
            {zoomLevel !== 1 && (
              <button 
                className="vc-zoom-btn zoom-reset" 
                onClick={handleZoomReset}
                onTouchEnd={(e) => { e.stopPropagation(); handleZoomReset(e); }}
                aria-label="Reset zoom"
                title="Reset to normal view"
              >
                ↻
              </button>
            )}
          </div>
          
          {/* Savings Badge */}
          {calculateSavings && car.status !== 'sold' && (
            <div className={`vc-savings-badge ${calculateSavings.isExclusive ? 'vc-exclusive-badge' : ''}`}>
              <div className="vc-savings-badge-amount">P{calculateSavings.amount.toLocaleString()}</div>
              <div className="vc-savings-badge-label">SAVE</div>
              <div className="vc-savings-tooltip">
                <div className="vc-savings-tooltip-header">
                  💰 {calculateSavings.isExclusive ? 'Exclusive Deal!' : 'Great Savings!'}
                </div>
                <div className="vc-savings-tooltip-row">
                  <span>Original Price:</span>
                  <span>P{calculateSavings.originalPrice.toLocaleString()}</span>
                </div>
                <div className="vc-savings-tooltip-row">
                  <span>Current Price:</span>
                  <span>P{car.price.toLocaleString()}</span>
                </div>
                <div className="vc-savings-tooltip-row">
                  <span>You Save:</span>
                  <span>P{calculateSavings.amount.toLocaleString()}</span>
                </div>
                {calculateSavings.description && (
                  <div className="vc-savings-tooltip-description">
                    {calculateSavings.description}
                  </div>
                )}
                {calculateSavings.validUntil && formatValidUntil(calculateSavings.validUntil) && (
                  <div className="vc-savings-tooltip-description">
                    Valid until: {formatValidUntil(calculateSavings.validUntil)}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Image Navigation */}
          {car.images && Array.isArray(car.images) && car.images.length > 1 && (
            <div className={`vc-image-navigation ${showNavigation ? 'show' : ''}`}>
              <button 
                className="vc-image-nav prev" 
                onClick={(e) => handleImageNavigation(e, 'prev')}
                aria-label="Previous image"
              >
                ❮
              </button>
              <button 
                className="vc-image-nav next" 
                onClick={(e) => handleImageNavigation(e, 'next')}
                aria-label="Next image"
              >
                ❯
              </button>
              <div className="vc-image-counter">
                {activeImageIndex + 1}/{car.images.length}
              </div>
            </div>
          )}
          
          {dealer?.verification?.isVerified && (
            <div className="vc-verified-badge">
              ✓ Verified
            </div>
          )}

          {/* Vehicle location / transit badge */}
          {(() => {
            const countryMap = {
              'BW': 'Botswana', 'BWA': 'Botswana', 'Botswana': 'Botswana',
              'ZA': 'South Africa', 'RSA': 'South Africa', 'SA': 'South Africa', 'S. Africa': 'South Africa', 'South Africa': 'South Africa',
              'ZW': 'Zimbabwe', 'ZWE': 'Zimbabwe', 'Zimbabwe': 'Zimbabwe',
              'NA': 'Namibia', 'NAM': 'Namibia', 'Namibia': 'Namibia',
              'ZM': 'Zambia', 'ZMB': 'Zambia', 'Zambia': 'Zambia',
              'MZ': 'Mozambique', 'MOZ': 'Mozambique', 'Mozambique': 'Mozambique',
              'JP': 'Japan', 'JPN': 'Japan', 'Japan': 'Japan',
            };
            if (car.transit?.isInTransit) {
              const destRaw = car.transit.destinationCountry || 'BW';
              const destName = countryMap[destRaw] || destRaw;
              const eta = car.transit.eta
                ? new Date(car.transit.eta).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
                : null;
              return (
                <div className="vc-location-badge vc-transit-badge">
                  🚢 In Transit to {destName}{eta ? <span className="vc-location-delivery"> · ETA {eta}</span> : null}
                </div>
              );
            }
            const defaultDelivery = {
              'South Africa': '5–7 day delivery',
              'Japan': '60–70 day delivery',
            };
            const raw = dealer?.location?.country || car?.location?.country || '';
            const countryName = countryMap[raw] || raw;
            if (!countryName) return null;
            const city = dealer?.location?.city || car?.location?.city;
            const locationLabel = city ? `${city}, ${countryName}` : countryName;
            const customDays = car?.location?.deliveryDays;
            const delivery = customDays || defaultDelivery[countryName];
            return (
              <div className="vc-location-badge">
                Vehicle in {locationLabel}{delivery ? <span className="vc-location-delivery"> · {delivery}</span> : null}
              </div>
            );
          })()}
        </div>
      </div>
      
      <div className="vc-content">
        <div className="vc-header">
          <div className="vc-title-section">
            <h4 className="vc-title">{car.title || 'Vehicle Listing'}</h4>
            <div className="vc-title-badges">
              {(car.priceType === 'negotiable') && (
                <div className="vc-negotiable-badge">Negotiable</div>
              )}
              {(car.priceType === 'call') && (
                <div className="vc-negotiable-badge">Call for Price</div>
              )}
              {car.priceOptions?.financeAvailable && dealer?.sellerType === 'dealership' && (
                <div
                  className="vc-finance-badge vc-finance-badge--clickable"
                  onClick={e => handleFlipToTab('finance', e)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && handleFlipToTab('finance', e)}
                  title="View finance details"
                >Finance Available ›</div>
              )}
              {car.priceOptions?.leaseAvailable && dealer?.sellerType === 'dealership' && (
                <div className="vc-lease-badge">Lease Available</div>
              )}
              {car.priceOptions?.excludesClearance && (
                <div className="vc-excl-badge">Excl. Clearance</div>
              )}
              {car.priceOptions?.excludesRegistration && (
                <div className="vc-excl-badge">Excl. Registration</div>
              )}
            </div>
          </div>
          <div className="vc-price-container">
            {calculateSavings && car.status !== 'sold' && (
              <div className="vc-original-price">P{calculateSavings.originalPrice.toLocaleString()}</div>
            )}
            <div className="vc-price">
              {(car.priceType === 'poa' || car.priceOptions?.showPriceAsPOA)
                ? 'POA'
                : car.priceType === 'call'
                ? 'Call for Price'
                : `P${car.price?.toLocaleString() || '0'}`}
            </div>
            {calculateSavings && car.status !== 'sold' && (
              <div className="vc-savings-highlight">
                You Save: P {calculateSavings.amount.toLocaleString()}
              </div>
            )}
            {car.priceOptions?.monthlyPayment && !car.priceOptions?.showPriceAsPOA && car.status !== 'sold' && (
              <div className="vc-monthly-payment">
                <span className="vc-monthly-payment-value">P {car.priceOptions.monthlyPayment.toLocaleString()} p/m</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="vc-specs">
          <div className="vc-spec-item">
            <span className="vc-spec-label">Year:</span>
            <span className="vc-spec-value">{car.specifications?.year || 'N/A'}</span>
          </div>
          <div className="vc-spec-item">
            <span className="vc-spec-label">Mileage:</span>
            <span className="vc-spec-value">{car.specifications?.mileage ? 
              `${car.specifications.mileage.toLocaleString()}km` : 'N/A'}</span>
          </div>
          <div className="vc-spec-item">
            <span className="vc-spec-label">Transmission:</span>
            <span className="vc-spec-value">{car.specifications?.transmission ? 
              car.specifications.transmission.charAt(0).toUpperCase() + car.specifications.transmission.slice(1) : 'N/A'}</span>
          </div>
          <div className="vc-spec-item">
            <span className="vc-spec-label">Fuel Type:</span>
            <span className="vc-spec-value">{car.specifications?.fuelType ? 
              car.specifications.fuelType.charAt(0).toUpperCase() + car.specifications.fuelType.slice(1) : 'N/A'}</span>
          </div>
        </div>
        
        {(car.serviceHistory?.hasServiceHistory || car.warranty || car.isCertified || car.video?.embedUrl || car.video?.directUrl) && (
          <div className="vc-badges">
            {car.serviceHistory?.hasServiceHistory && (
              <div className="vc-service-badge">Service History</div>
            )}
            {car.warranty && dealer?.sellerType === 'dealership' && (
              <div className="vc-warranty-badge">Warranty</div>
            )}
            {car.isCertified && dealer?.sellerType === 'dealership' && (
              <div className="vc-certified-badge">Certified</div>
            )}
            {(car.video?.embedUrl || car.video?.directUrl) && (
              <div className="vc-video-badge" onClick={e => { e.stopPropagation(); setBackTab('video'); handleFlip(); }}>▶ Video</div>
            )}
          </div>
        )}
        
        <div className={`vc-dealer-info ${dealer?.sellerType === 'private' ? 'private-seller' : 'dealership'}`}
             onClick={dealer?.sellerType === 'dealership' ? handleDealerClick : undefined}>
          
          {(() => {
            const getInitials = () => {
              const name = dealer?.businessName || dealer?.name || 'Unknown';
              return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().substring(0, 2);
            };

            const possibleSources = [
              dealer?.avatar?.url,
              dealer?.avatar,
              dealer?.logo?.url,
              dealer?.logo,
              dealer?.profilePicture?.url,
              dealer?.profilePicture,
              car?.dealer?.profile?.logo?.url,
              car?.dealer?.profile?.logo,
              car?.dealer?.profile?.avatar?.url,
              car?.dealer?.profile?.avatar,
              car?.dealer?.logo?.url,
              car?.dealer?.logo,
              car?.dealer?.avatar?.url,
              car?.dealer?.avatar,
              car?.dealer?.profilePicture?.url,
              car?.dealer?.profilePicture,
            ];
            
            let imageSource = null;
            for (const source of possibleSources) {
              if (source && 
                  typeof source === 'string' && 
                  source.trim() !== '' && 
                  source !== 'undefined' && 
                  source !== 'null' &&
                  !source.includes('undefined') &&
                  !source.includes('null')) {
                imageSource = source;
                break;
              }
            }
            
            if (!imageSource || dealerImageError) {
              if (dealer?.sellerType === 'marketplace') return null;
              return (
                <div className="vc-dealer-avatar-placeholder">
                  {getInitials()}
                </div>
              );
            }
            
            return (
              <img 
                className="vc-dealer-avatar" 
                src={imageSource}
                alt={dealer?.businessName || dealer?.name || 'Seller'}
                onError={(e) => {
                  if (dealerImageError) return;
                  
                  if (!e.target.src.includes('/images/placeholders/')) {
                    e.target.src = '/images/placeholders/avatar.jpg';
                    return;
                  }
                  
                  setDealerImageError(true);
                }}
                onLoad={() => {
                  if (dealerImageError) {
                    setDealerImageError(false);
                  }
                }}
              />
            );
          })()}

          <div className="vc-dealer-details">
            <div className="vc-dealer-name">
              {dealer?.sellerType === 'marketplace' ? (() => {
                const raw = dealer.marketplace?.sourceCountry || dealer?.location?.country || '';
                const names = {'za':'South Africa','jp':'Japan','cn':'China','ae':'United Arab Emirates','gb':'United Kingdom','us':'United States','de':'Germany','au':'Australia','bw':'Botswana','sg':'Singapore','uae':'United Arab Emirates','dubai':'United Arab Emirates','uk':'United Kingdom','usa':'United States'};
                const k = raw.toLowerCase().trim();
                const fullName = names[k] || raw;
                return fullName ? `${fullName} Inventory` : 'International Inventory';
              })() : (dealer?.businessName || dealer?.name || 'Unknown Seller')}
              {(dealer?.verification?.isVerified || dealer?.sellerType === 'marketplace') && (
                <span className="vc-verified-icon" title="Verified">✓</span>
              )}
            </div>
            
            <div className="vc-dealer-meta">
              <span className={`vc-seller-type ${dealer?.sellerType || 'dealership'}`}>
                {dealer?.sellerTypeLabel || (dealer?.sellerType === 'private' ? 'Private Seller' : 'Dealership')}
              </span>
              {dealer?.sellerType !== 'marketplace' && (
                <span className="vc-dealer-location">
                  {dealer?.location?.city || 'Unknown Location'}
                  {dealer?.location?.country && dealer.location.country !== 'Botswana' ? (
                    <>{', '}{dealer.location.country}</>
                  ) : null}
                </span>
              )}
            </div>
            
            <div className="vc-dealer-actions">
              {dealer.id && dealer.sellerType === 'dealership' && (
                <span className="vc-dealer-link">View Dealership</span>
              )}
              {dealer.sellerType === 'private' && dealer.privateSeller?.preferredContactMethod && (
                <span className="vc-contact-preference">
                  Contact: {dealer.privateSeller.preferredContactMethod === 'phone' ? 'Phone' : 
                           dealer.privateSeller.preferredContactMethod === 'whatsapp' ? 'WhatsApp' : 'Phone/WhatsApp'}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="vc-footer">
          <span className={`vc-condition-tag ${(car.condition || 'used').toLowerCase()}`}>
            {car.condition ? 
              car.condition.charAt(0).toUpperCase() + car.condition.slice(1).toLowerCase() : 
              'Used'}
          </span>
          <div className="vc-actions">
            <button
              className="vc-share-btn"
              onClick={handleShareClick}
              aria-label="Share"
            >
              Share
            </button>
            <button
              className="vc-reserve-btn"
              onClick={handleReserveClick}
              aria-label="WhatsApp"
              disabled={car.status === 'sold'}
            >
              {car.status === 'sold' ? 'Sold' : 'WhatsApp'}
            </button>
            <button className="vc-details-btn">
              View Details
            </button>
          </div>
        </div>
      </div>{/* vc-content */}
      </div>{/* vc-card-front */}

      {/* ── BACK FACE: Reviews / Finance / Service History ── */}
      <div className="vc-card-face vc-card-back" onClick={e => e.stopPropagation()}>
        <div className="vc-back-top-bar">
          <button className="vc-flip-back-btn" onClick={handleFlip}>← Back</button>
          <div className="vc-back-tabs">
            <button
              className={`vc-back-tab${backTab === 'reviews' ? ' active' : ''}`}
              onClick={e => { e.stopPropagation(); setBackTab('reviews'); }}
            >Reviews</button>
            <button
              className={`vc-back-tab${backTab === 'finance' ? ' active' : ''}`}
              onClick={e => { e.stopPropagation(); setBackTab('finance'); }}
            >Finance</button>
            <button
              className={`vc-back-tab${backTab === 'service-history' ? ' active' : ''}`}
              onClick={e => { e.stopPropagation(); setBackTab('service-history'); }}
            >Service History</button>
            {(car.video?.embedUrl || car.video?.directUrl) && (
              <button
                className={`vc-back-tab${backTab === 'video' ? ' active' : ''}`}
                onClick={e => { e.stopPropagation(); setBackTab('video'); }}
              >▶ Video</button>
            )}
          </div>
        </div>

        {/* ── Finance tab ── */}
        {backTab === 'finance' && (
          <div className="vc-reviews-scroll vc-finance-panel">
            {car.priceOptions?.financeAvailable ? (
              <>
                <div className="vc-finance-header">
                  <span className="vc-finance-icon">%</span>
                  <div>
                    <p className="vc-finance-title">Finance Available</p>
                    <p className="vc-finance-sub">Through this dealership</p>
                  </div>
                </div>
                <div className="vc-finance-rows">
                  <div className="vc-finance-row">
                    <span className="vc-finance-label">Vehicle Price</span>
                    <span className="vc-finance-value">
                      {car.priceOptions?.showPriceAsPOA ? 'POA' : `P${(car.price || 0).toLocaleString()}`}
                    </span>
                  </div>
                  {car.priceOptions?.monthlyPayment && (
                    <div className="vc-finance-row vc-finance-row--highlight">
                      <span className="vc-finance-label">Est. Monthly Payment</span>
                      <span className="vc-finance-value">P{Number(car.priceOptions.monthlyPayment).toLocaleString()}<span className="vc-finance-unit">/mo</span></span>
                    </div>
                  )}
                  {car.priceOptions?.includesVAT && (
                    <div className="vc-finance-row">
                      <span className="vc-finance-label">VAT</span>
                      <span className="vc-finance-value vc-finance-green">Included</span>
                    </div>
                  )}
                  {car.priceOptions?.leaseAvailable && (
                    <div className="vc-finance-row">
                      <span className="vc-finance-label">Lease</span>
                      <span className="vc-finance-value vc-finance-green">Also available</span>
                    </div>
                  )}
                </div>
                <p className="vc-finance-disclaimer">
                  Finance terms subject to credit approval. Contact the dealership for a personalised quote.
                </p>
                {(dealer?.contact?.phone || dealer?.contact?.whatsapp) && (
                  <a
                    href={`tel:${dealer.contact.phone || dealer.contact.whatsapp}`}
                    className="vc-finance-cta"
                    onClick={e => e.stopPropagation()}
                  >
                    Call for Finance Quote
                  </a>
                )}
              </>
            ) : (
              <div className="vc-no-reviews">Finance details not available for this listing.</div>
            )}
          </div>
        )}

        {/* ── Service History tab ── */}
        {backTab === 'service-history' && (
          <div className="vc-reviews-scroll vc-sh-panel">
            {car.serviceHistory?.hasServiceHistory ? (
              <>
                <div className="vc-finance-header">
                  <span className="vc-finance-icon">✓</span>
                  <div>
                    <p className="vc-finance-title">Full Service History</p>
                    <p className="vc-finance-sub">{(car.serviceHistory.records || []).length} record{(car.serviceHistory.records || []).length !== 1 ? 's' : ''} on file</p>
                  </div>
                </div>
                {(car.serviceHistory.records || []).length > 0 ? (
                  <div className="vc-sh-records">
                    {car.serviceHistory.records.map((rec, i) => (
                      <div key={i} className="vc-sh-record">
                        <div className="vc-sh-record-header">
                          <span className="vc-sh-date">{rec.date ? new Date(rec.date).toLocaleDateString() : '—'}</span>
                          {rec.mileage && <span className="vc-sh-mileage">{Number(rec.mileage).toLocaleString()} km</span>}
                        </div>
                        {rec.serviceType && <p className="vc-sh-type">{rec.serviceType}</p>}
                        {rec.provider && <p className="vc-sh-provider">{rec.provider}</p>}
                        {rec.notes && <p className="vc-sh-notes">{rec.notes}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="vc-finance-disclaimer">Service history confirmed but no individual records uploaded yet.</p>
                )}
              </>
            ) : (
              <div className="vc-no-reviews">No service history recorded for this vehicle.</div>
            )}
          </div>
        )}

        {/* ── Video tab ── */}
        {backTab === 'video' && (
          <div className="vc-reviews-scroll vc-video-panel">
            {car.video?.embedUrl ? (
              <div className="vc-video-embed-wrap">
                <iframe
                  src={car.video.embedUrl}
                  title="Vehicle Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="vc-video-iframe"
                />
              </div>
            ) : car.video?.directUrl ? (
              <video controls className="vc-video-iframe" style={{ background: '#000' }}>
                <source src={car.video.directUrl} />
              </video>
            ) : (
              <div className="vc-no-reviews">No video available for this vehicle.</div>
            )}
            {car.video?.label && (
              <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.4rem' }}>
                via {car.video.label}
              </p>
            )}
          </div>
        )}

        {/* ── Reviews tab ── */}
        {backTab === 'reviews' && (
        <>
        <div className="vc-reviews-scroll">
          {reviewsLoading && <div className="vc-reviews-loading">Loading…</div>}
          {reviewsError && <div className="vc-reviews-error">{reviewsError}</div>}
          {!reviewsLoading && !reviewsError && reviews.length === 0 && (
            <div className="vc-no-reviews">No reviews yet — be the first!</div>
          )}
          {reviews.map((review) => (
            <div key={review._id} className="vc-review-item">
              {/* Review header */}
              <div className="vc-review-header">
                <div className="vc-review-author-row">
                  <span className="vc-review-author">{review.userName || 'Anonymous'}</span>
                  {review.isOwner && <span className="vc-owner-badge">Owner</span>}
                </div>
                <span className="vc-review-stars">{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}</span>
              </div>
              {review.review && <p className="vc-review-text">{review.review}</p>}
              {/* Reactions + Reply row */}
              <div className="vc-review-actions">
                <div className="vc-react-group">
                  <button
                    className={`vc-react-btn ${review.likes?.includes(user?._id) ? 'active-like' : ''}`}
                    onClick={e => { e.stopPropagation(); handleReact(review._id, 'like'); }}
                    title="Helpful"
                  >👍 <span>{review.likes?.length || 0}</span></button>
                  <button
                    className={`vc-react-btn ${review.dislikes?.includes(user?._id) ? 'active-dislike' : ''}`}
                    onClick={e => { e.stopPropagation(); handleReact(review._id, 'dislike'); }}
                    title="Not helpful"
                  >👎 <span>{review.dislikes?.length || 0}</span></button>
                </div>
                <button
                  className="vc-reply-btn"
                  onClick={e => { e.stopPropagation(); setReplyingTo(replyingTo === review._id ? null : review._id); setReplyText(''); }}
                >Reply</button>
                {review.date && <span className="vc-review-date">{new Date(review.date).toLocaleDateString()}</span>}
              </div>

              {/* Replies */}
              {review.replies?.length > 0 && (
                <div className="vc-replies-list">
                  {review.replies.map(rep => (
                    <div key={rep._id} className={`vc-reply-item ${rep.isOwner ? 'vc-reply-owner' : ''}`}>
                      <div className="vc-reply-header">
                        <div className="vc-review-author-row">
                          <span className="vc-reply-author">{rep.userName || 'Anonymous'}</span>
                          {rep.isOwner && <span className="vc-owner-badge">Owner</span>}
                        </div>
                        <span className="vc-review-date">{new Date(rep.date).toLocaleDateString()}</span>
                      </div>
                      <p className="vc-reply-text">{rep.text}</p>
                      <div className="vc-react-group vc-react-group--small">
                        <button
                          className={`vc-react-btn vc-react-btn--sm ${rep.likes?.includes(user?._id) ? 'active-like' : ''}`}
                          onClick={e => { e.stopPropagation(); handleReact(review._id, 'like', rep._id); }}
                        >👍 <span>{rep.likes?.length || 0}</span></button>
                        <button
                          className={`vc-react-btn vc-react-btn--sm ${rep.dislikes?.includes(user?._id) ? 'active-dislike' : ''}`}
                          onClick={e => { e.stopPropagation(); handleReact(review._id, 'dislike', rep._id); }}
                        >👎 <span>{rep.dislikes?.length || 0}</span></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Inline reply form */}
              {replyingTo === review._id && (
                <div className="vc-reply-form" onClick={e => e.stopPropagation()}>
                  <textarea
                    className="vc-review-textarea"
                    placeholder="Write a reply…"
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    rows={2}
                    onClick={e => e.stopPropagation()}
                  />
                  <div className="vc-reply-form-actions">
                    <button className="vc-reply-cancel-btn" onClick={e => { e.stopPropagation(); setReplyingTo(null); }}>Cancel</button>
                    <button
                      className="vc-reply-submit-btn"
                      onClick={e => { e.stopPropagation(); handleSubmitReply(review._id); }}
                      disabled={submittingReply || replyText.trim().length < 2}
                    >{submittingReply ? 'Posting…' : 'Post Reply'}</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="vc-review-form">
          <div className="vc-star-picker">
            {[1, 2, 3, 4, 5].map(s => (
              <button key={s} className={`vc-star-btn ${s <= newRating ? 'active' : ''}`}
                onClick={e => { e.stopPropagation(); setNewRating(s); }}>★</button>
            ))}
          </div>
          <textarea
            className="vc-review-textarea"
            placeholder="Share your experience… (min 10 chars)"
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            rows={2}
            onClick={e => e.stopPropagation()}
          />
          {submitError && <span className="vc-submit-error">{submitError}</span>}
          {submitSuccess && <span className="vc-submit-success">✓ Review submitted!</span>}
          <button
            className="vc-review-submit"
            onClick={handleSubmitReview}
            disabled={isSubmitting || !newRating || newComment.trim().length < 10}
          >{isSubmitting ? 'Submitting…' : 'Submit Review'}</button>
        </div>
        </>
        )}
      </div>{/* vc-card-back */}

      </div>{/* vc-card-flipper */}
    </div>{/* vc-card */}

    <button className="vc-review-tab" onClick={handleFlip} title="Listing reviews">
      REVIEW
      {previewCount !== null && (
        <span className={`vc-review-tab-badge ${previewCount > 0 ? 'has-reviews' : ''}`}>
          {previewCount > 0 && <span className="vc-review-tab-dot" />}
          {previewCount}
        </span>
      )}
    </button>

    </div>
  );
};

export default React.memo(VehicleCard);